import { useLayoutEffect, useState, type CSSProperties, type RefObject } from 'react'

/**
 * Positions a dropdown menu against its trigger, in viewport (fixed) coordinates.
 *
 * Menus were rendered with `position: absolute` inside the page, so any ancestor
 * with `overflow` (the scrolling main area, a table's horizontal scroller) clipped
 * them — the menu vanished behind the sidebar or was cut off. Rendering the menu
 * in a portal with these fixed coordinates escapes every clip and stacking trap.
 * The menu flips to stay on-screen and tracks scroll/resize.
 *
 * The returned style ALWAYS carries a `maxHeight`, so the menu element must set
 * an overflow rule of its own. Without one the content past the cap paints
 * outside the panel — no background, no border, no shadow under it — which reads
 * as a broken dropdown rather than a clipped one.
 */
export function useAnchoredMenu(
  open: boolean,
  triggerRef: RefObject<HTMLElement | null>,
  options: { width?: number; align?: 'left' | 'right'; maxHeight?: number } = {},
): CSSProperties | undefined {
  const [style, setStyle] = useState<CSSProperties>()

  useLayoutEffect(() => {
    if (!open) return
    const trigger = triggerRef.current
    if (!trigger) return

    const update = () => {
      const rect = trigger.getBoundingClientRect()
      const gap = 4
      const margin = 8
      const menuWidth = options.width ?? rect.width
      const viewport = window.innerWidth

      // Default alignment, then nudge back on-screen from either edge.
      let left = options.align === 'right' ? rect.right - menuWidth : rect.left
      if (left + menuWidth > viewport - margin) left = viewport - margin - menuWidth
      if (left < margin) left = margin

      // Prefer opening downward; flip up if there isn't room below.
      //
      // The comparison is against the menu's OWN height, not a constant. It used
      // to test `< 240`, which is shorter than several menus in the app — a
      // 288px combobox list with 264px beneath it therefore stayed downward and
      // hung 24px past the bottom of the window, which is exactly the "I had to
      // scroll to pick an option" case.
      const wanted = options.maxHeight ?? 240
      const belowSpace = window.innerHeight - rect.bottom - gap - margin
      const aboveSpace = rect.top - gap - margin
      const openUp = belowSpace < wanted && aboveSpace > belowSpace

      // Then cap it to the side it actually opens on, so it can never run off
      // the screen even when neither side can hold it whole — it scrolls inside
      // itself instead, which is what `overflow-y-auto` on the list is for.
      const room = Math.max(120, openUp ? aboveSpace : belowSpace)

      setStyle({
        position: 'fixed',
        top: openUp ? undefined : rect.bottom + gap,
        bottom: openUp ? window.innerHeight - rect.top + gap : undefined,
        maxHeight: Math.min(wanted, room),
        left,
        // The menu must actually BE as wide as the width we positioned it with.
        // With `minWidth: rect.width` a right-aligned menu was placed at
        // `rect.right - options.width` but then shrank to fit its content, so its
        // right edge stopped short of the trigger — a ⋮ button 28px wide with a
        // 176px hint left the box floating 60-odd pixels to its left.
        minWidth: options.width ?? rect.width,
        zIndex: 60,
      })
    }

    update()
    window.addEventListener('resize', update)
    // capture: also fire for scrolls inside nested scroll containers.
    window.addEventListener('scroll', update, true)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [open, triggerRef, options.width, options.align, options.maxHeight])

  return style
}
